import React, { useState } from "react";
import { useTranslation } from "react-i18next";

const REASON_OPTIONS = ["TRANSFERRED", "GRADUATED", "REQUESTED_BY_STUDENT", "DISCIPLINARY", "OTHER"];

const DepartStudentModal = ({ isOpen, student, isSubmitting, onClose, onConfirm }) => {
  const { t } = useTranslation();
  const [reasonPreset, setReasonPreset] = useState("TRANSFERRED");
  const [reasonCustom, setReasonCustom] = useState("");
  // Override the lockout guard. When the student has no verified personal email
  // on file, departing would strand them once their school email dies, so the
  // backend blocks it. The admin can explicitly acknowledge + override.
  const [override, setOverride] = useState(false);

  if (!isOpen || !student) return null;

  const studentName = `${student.firstName || ""} ${student.lastName || ""}`.trim() || student.slug || student.email || t("modals.departStudent.thisStudent");
  const needsPersonalEmail = !student.hasPersonalEmail;
  const blockedByEmail = needsPersonalEmail && !override;

  const handleConfirm = () => {
    if (reasonPreset === "OTHER" && !reasonCustom.trim()) {
      return;
    }
    if (blockedByEmail) {
      return;
    }
    onConfirm(reasonPreset, reasonCustom.trim(), needsPersonalEmail ? override : false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-white rounded-2xl p-6 font-['Manrope']">
        <h2 className="text-xl font-bold text-gray-900">{t("modals.departStudent.title")}</h2>
        <p className="text-sm text-gray-600 mt-2">{t("modals.departStudent.subtitle", { studentName })}</p>

        <div className="mt-5">
          <label className="text-sm font-medium text-gray-700">{t("modals.departStudent.reason")}</label>
          <select
            value={reasonPreset}
            onChange={(e) => setReasonPreset(e.target.value)}
            className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2.5 bg-white"
          >
            {REASON_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {t(`modals.departStudent.reasons.${option.toLowerCase()}`)}
              </option>
            ))}
          </select>
        </div>

        {/* Contextual consequences — emphasized for Graduated (which grants a
            permanent verified-graduation credential + emails the student). */}
        <div
          className={`mt-4 rounded-lg border p-3 text-sm leading-relaxed ${
            reasonPreset === "GRADUATED"
              ? "border-emerald-200 bg-emerald-50 text-emerald-900"
              : "border-gray-200 bg-gray-50 text-gray-600"
          }`}
        >
          {reasonPreset === "GRADUATED"
            ? t("modals.departStudent.notes.graduated", { studentName })
            : t("modals.departStudent.notes.default", { studentName })}
        </div>

        {reasonPreset === "OTHER" ? (
          <div className="mt-4">
            <label className="text-sm font-medium text-gray-700">{t("modals.departStudent.customReason")}</label>
            <textarea
              rows={3}
              value={reasonCustom}
              onChange={(e) => setReasonCustom(e.target.value)}
              placeholder={t("modals.departStudent.customReasonPlaceholder")}
              className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2.5"
            />
          </div>
        ) : null}

        {needsPersonalEmail ? (
          <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-3">
            <p className="text-sm font-semibold text-amber-900">
              {t("modals.departStudent.noPersonalEmail.title", { defaultValue: "This student hasn't added a personal email yet" })}
            </p>
            <p className="mt-1 text-sm text-amber-800 leading-relaxed">
              {t("modals.departStudent.noPersonalEmail.body", {
                defaultValue:
                  "Their login is their school email, which they'll lose access to. Ask them to add a personal email in their Hypebook settings first — then depart moves their login to it automatically.",
              })}
            </p>
            <label className="mt-3 flex items-start gap-2 text-sm text-amber-900">
              <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} className="mt-0.5" />
              <span>
                {t("modals.departStudent.noPersonalEmail.override", {
                  defaultValue: "Depart anyway — I understand this student may lose access to their account.",
                })}
              </span>
            </label>
          </div>
        ) : null}

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-60"
          >
            {t("modals.common.cancel")}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isSubmitting || (reasonPreset === "OTHER" && !reasonCustom.trim()) || blockedByEmail}
            className="px-4 py-2 rounded-lg bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-60"
          >
            {isSubmitting ? t("modals.departStudent.departing") : t("modals.departStudent.confirm")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DepartStudentModal;
